import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import SuccessAnimation from '../copmponents/SuccessAnimation';
import PaymentFailure from '../copmponents/PaymentFailure';
import { isMobileDevice } from '../../util/device';

const MutualAidFundPage = () => {
	const [currentStep, setCurrentStep] = useState('content');
	const [statusMessage, setStatusMessage] = useState('');
	const [isCheckingFund, setIsCheckingFund] = useState(true);
	const [isMemberOfFund, setIsMemberOfFund] = useState(false);
	const [isLoading, setIsLoading] = useState(false);
	const navigate = useNavigate();

	useEffect(() => {
		let isMounted = true;

		const checkFundStatus = async () => {
			try {
				const response = await axios.get('dashboard/member/member_self');
				const data = response?.data;
				const activeAdvocacy =
					data?.activeAdvocacy ??
					data?.member?.activeAdvocacy ??
					data?.data?.activeAdvocacy ??
					false;

				if (activeAdvocacy) {
					setIsMemberOfFund(true);
				}
			} catch (error) {
				// If the request fails, keep the user on the page.
			}

			if (isMounted) {
				setIsCheckingFund(false);
			}
		};

		checkFundStatus();

		return () => {
			isMounted = false;
		};
	}, [navigate]);

	if (isCheckingFund) {
		return null;
	}

	if (currentStep === 'success') {
		return (
			<SuccessAnimation
				onNext={() => setCurrentStep('content')}
				titlerror={statusMessage || 'התשלום בוצע בהצלחה'}
			/>
		);
	}

	if (currentStep === 'error') {
		return (
			<PaymentFailure
				onClose={() => setCurrentStep('content')}
				error={statusMessage}
				titlerror="התשלום נכשל"
			/>
		);
	}

	const handlePayment = async () => {
		setIsLoading(true);
		try {
			const response = await axios.get(
				`/member/dues/createRecurring/ADVOCACY/SELF`,
				{
					headers: { 'Content-Type': 'application/json' }
				}
			);

			if (response?.status === 200) {
				const data = response?.data;
				if (typeof data === 'string') {
					setStatusMessage(data);
					setCurrentStep('success');
				} else if (response?.data?.urlPage) {
					window.location.href = response?.data?.urlPage;
				} else {
					setStatusMessage('קשה לעסקה עם שרת התשלום');
					setCurrentStep('error');
				}
			} else {
				setStatusMessage(response.response?.data?.message || 'שגיאה בתשלום');
				setCurrentStep('error');
			}
		} catch (error) {
			setStatusMessage(
				error?.response?.data?.message || 'שגיאה בתשלום, אנא נסה שוב'
			);
			setCurrentStep('error');
		} finally {
			setIsLoading(false);
		}
	};

	const isMobile = isMobileDevice();

	return (
		<div className="bg-white p-4 min-h-[70vh]" dir="rtl">
			<div className="bg-white rounded-xl shadow-lg w-full max-w-md mx-auto p-6 relative">
				{isMemberOfFund && (
					<div className="absolute inset-0 bg-white/80 backdrop-blur-[1px] rounded-xl flex items-center justify-center p-6">
						<button
							onClick={() => navigate(isMobile ? '/Mobile' : '/')}
							className="w-full bg-gradient-to-l from-blue-600 to-sky-500 text-white py-3 rounded-xl font-semibold shadow-md hover:shadow-lg hover:from-blue-700 hover:to-sky-600 transition-all duration-200 ring-1 ring-blue-200"
						>
							ממונה יקר, אתה כבר רשום
						</button>
					</div>
				)}

				{/* Title */}
				<h2 className="text-lg font-bold text-gray-800 text-center mb-3">
					הקרן לסיוע הדדי (קל"ה)
				</h2>

				{/* Body */}
				<p className="text-gray-600 text-sm leading-relaxed mb-5 text-center">
					לשכת ממוני הבטיחות מזמינה אותך,
					<br />
					להצטרף ולהיות חבר/ה בקל"ה
				</p>

				{/* Bullet points */}
				<ul className="text-gray-600 text-sm mb-5 space-y-1 text-right">
					<li className="flex items-start gap-2 justify-end">
						<span className="text-green-500">✔</span>
						<span>
							קרן לסיוע הדדי (קל"ה) מיסודה של עמותת לשכת ממוני הבטיחות בישראל
						</span>
					</li>
					<li className="flex items-start gap-2 justify-end">
						<span className="text-green-500">✔</span>
						<span>
							מסייעת לחברים להתמודד עם השלכות משפטיות של מימון עורך דין בהליך
							פלילי
						</span>
					</li>
					<li className="flex items-start gap-2 justify-end">
						<span className="text-green-500">✔</span>
						<span>
							החברות בקרן הינה לשנה קלנדרית בלבד ותקפה עד 31 בחודש דצמבר
							לאותה שנה
						</span>
					</li>
					<li className="flex items-start gap-2 justify-end">
						<span className="text-green-500">✔</span>
						<span>
							הוראת קבע פעילה תאפשר את תשלום דמי החברות בקל"ה באופן אוטומטי
							ותסייע בשמירה על רצף זכאות למקרה הצורך
						</span>
					</li>
					<li className="flex items-start gap-2 justify-end">
						<span className="text-green-500">✔</span>
						<span>
							הוראת הקבע בגין דמי החברות מתחדשת אוטומטית בחודש ינואר בכל שנה
							הקלנדרית הבאה - כל עוד הוראת הקבע תקינה
						</span>
					</li>
					<li className="flex items-start gap-2 justify-end">
						<span className="text-green-500">✔</span>
						<span>הפעלת סיוע הקרן בהתאם לתנאים המפורטים בנוהל 009 של העמותה</span>
					</li>
				</ul>

				<p className="text-gray-700 text-sm text-center leading-relaxed mb-3">
					"ביחד נעשה ונצליח"
					<br />
					בברכה
					<br />
					לשכת ממוני הבטיחות - מקצוענים זה אנחנו
				</p>

				{/* Pay button */}
				<button
					onClick={handlePayment}
					disabled={isLoading || isMemberOfFund}
					className="w-full bg-blue-600 text-white py-2 rounded-lg font-semibold hover:bg-blue-700 transition shadow disabled:bg-gray-300"
				>
					{isLoading ? 'מעבר לתשלום' : 'מעבר לתשלום'}
				</button>

				{/* Later */}
				<button
					onClick={() => navigate(isMobile ? '/Mobile' : '/')}
					className="w-full text-gray-500 text-xs mt-3 underline"
				>
					לא עכשיו
				</button>
			</div>
		</div>
	);
};

export default MutualAidFundPage;
